/**
 * invites/{id} — Director-issued email invitations.
 *
 * Tracks who's been pre-invited so the Director can see outstanding
 * invites in Manage Users. The actual email is sent by the invite
 * Netlify function; this module handles the client side.
 */

import { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
  getDocs,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';
import type { ManagerTitle, UserRole } from './users';

export interface InviteDoc {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
  /** The manager flavor the Director picked, when role is 'manager'. */
  managerTitle?: ManagerTitle | null;
  status: 'pending' | 'accepted';
  /**
   * Students (dancers) to auto-link to this account when the invite is
   * accepted. Lets the Director "sync" a family's kids to their account up
   * front, so siblings show up together the moment the parent signs in —
   * no manual linking by the family afterwards.
   */
  studentIds?: string[];
  createdBy: string;
  createdAt: Timestamp | FieldValue;
}

/** Subscribe to all outstanding invites (Director only). */
export function useInvites(): { loading: boolean; invites: InviteDoc[]; error: string | null } {
  const [invites, setInvites] = useState<InviteDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      return;
    }
    // Equality-only query on purpose: adding orderBy('createdAt') here would
    // demand a (status, createdAt) composite index that was never committed to
    // shared/firestore.indexes.json, and the index deploy pipeline is down -
    // the same disease as the staff dancer attendance card. Sort client-side;
    // the pending-invite list is tiny.
    const q = query(collection(db, 'invites'), where('status', '==', 'pending'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<InviteDoc, 'id'>) }));
        rows.sort((a, b) => {
          const am = typeof (a.createdAt as Timestamp)?.toMillis === 'function' ? (a.createdAt as Timestamp).toMillis() : 0;
          const bm = typeof (b.createdAt as Timestamp)?.toMillis === 'function' ? (b.createdAt as Timestamp).toMillis() : 0;
          return bm - am;
        });
        setInvites(rows);
        setLoading(false);
      },
      (err) => {
        // Keep Firestore's own words off the Director's screen - they carry
        // the project id and index URLs, which are noise to her and homework
        // to nobody. The detail goes to the console for diagnosis.
        console.error('[invites] pending-invite listener failed', err);
        setError("Couldn't check pending invites right now. Try again in a moment.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  return { loading, invites, error };
}

export async function cancelInvite(id: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await deleteDoc(doc(db, 'invites', id));
}

/**
 * Check if the currently signed-in user (verified email) has a pending
 * invite. Returns the invite if found, null otherwise.
 */
export async function findMyInvite(): Promise<InviteDoc | null> {
  if (!db) return null;
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user || !user.emailVerified || !user.email) return null;
  const q = query(
    collection(db, 'invites'),
    where('email', '==', user.email.toLowerCase()),
    where('status', '==', 'pending')
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...(d.data() as Omit<InviteDoc, 'id'>) };
}

/**
 * Claim a pending invite for the currently signed-in user (verified email
 * required). Calls the acceptInvite Netlify function which sets the custom
 * claim + updates the users doc. Returns the role that was granted.
 */
export async function acceptMyInvite(): Promise<{ ok: boolean; role?: string; reason?: string }> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in required.');
  const token = await user.getIdToken();
  const res = await fetch('/.netlify/functions/acceptInvite', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  // The function reports failures in two shapes: {ok:false, reason} for the
  // not-configured case, and {error: '...'} with an HTTP status for everything
  // else (bad email, duplicate pending invite, director only). The caller
  // displays `reason`, so an {error} response used to surface as the useless
  // "Something went wrong" while the actual explanation sat unread in the
  // body. Normalise both shapes into `reason` so the person tapping the
  // button reads what the server actually said.
  const data = await res.json().catch(() => ({}));
  if (data && data.ok) return data;
  const reason =
    (typeof data?.reason === 'string' && data.reason) ||
    (typeof data?.error === 'string' && data.error) ||
    `The invite could not be saved (HTTP ${res.status}). Please try again.`;
  return { ok: false, reason };
}

/**
 * Send an invitation email + create the invite doc. Calls the Netlify
 * function so the email key stays server-side. Returns the created
 * invite id on success.
 */
export async function sendInvite(
  email: string,
  name: string,
  role: UserRole,
  studentIds: string[] = [],
  managerTitle?: ManagerTitle | null
): Promise<{ ok: boolean; inviteId?: string; emailSent?: boolean; reason?: string }> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in required.');
  const token = await user.getIdToken();
  const res = await fetch('/.netlify/functions/invite', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
      name: name.trim(),
      role,
      studentIds,
      managerTitle,
    }),
  });
  // Same normalisation as acceptMyInvite above, for the same reason: the
  // function answers {ok:false, reason} for the not-configured case but
  // {error} with an HTTP status for everything else, including the useful
  // ones like the duplicate-pending 409. ManageUsers displays `reason`, so
  // an {error} response used to surface as "Something went wrong" while the
  // real explanation sat unread in the body. This is the form Bobbie was
  // actually tapping; the first version of this fix anchored on identical
  // closing lines and landed in acceptMyInvite instead, which Codex caught.
  const data = await res.json().catch(() => ({}));
  if (data && data.ok) return data;
  const reason =
    (typeof data?.reason === 'string' && data.reason) ||
    (typeof data?.error === 'string' && data.error) ||
    `The invite could not be saved (HTTP ${res.status}). Please try again.`;
  return { ok: false, reason };
}
